"use client";

import { useRouter, useSearchParams, usePathname } from "next/navigation";

interface UseSearchParamsWithPathReturn {
  updateParam: (key: string, value: string) => void;
  removeParam: (key: string) => void;
  getParam: (key: string) => string | null;
  updateMultipleParams: (params: Record<string, string>) => void;
  updatePathParam: (paramName: string, value: string) => void;
  getPathParam: (paramName: string) => string | null;
  updatePathAndParams: (
    pathParamName: string,
    pathValue: string,
    queryParams: Record<string, string>
  ) => void;
}

export function useSearchParamsWithPath(): UseSearchParamsWithPathReturn {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const updateParam = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set(key, value);
    router.push(`${pathname}?${params.toString()}`);
  };

  const updateMultipleParams = (newParams: Record<string, string>) => {
    const params = new URLSearchParams(searchParams.toString());
    Object.entries(newParams).forEach(([key, value]) => {
      params.set(key, value);
    });
    router.push(`${pathname}?${params.toString()}`);
  };

  const removeParam = (key: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete(key);
    router.push(`${pathname}?${params.toString()}`);
  };

  const getParam = (key: string) => {
    return searchParams.get(key);
  };

  const updatePathParam = (paramName: string, value: string) => {
    // For the search/city/[state--city] route, we need to update the path parameter
    if (pathname.includes("/search/city/")) {
      const newPath = pathname.replace(
        /\/search\/city\/[^/]+/,
        `/search/city/${value}`
      );
      const queryString = searchParams.toString();
      const newUrl = queryString ? `${newPath}?${queryString}` : newPath;
      router.push(newUrl);
    }
  };

  const getPathParam = (paramName: string) => {
    // Extract path parameters from the current pathname
    if (pathname.includes("/search/city/")) {
      const match = pathname.match(/\/search\/city\/([^/]+)/);
      return match ? match[1] : null;
    }
    return null;
  };

  const updatePathAndParams = (
    pathParamName: string,
    pathValue: string,
    queryParams: Record<string, string>
  ) => {
    // For the search/city/[state--city] route, update both path and query parameters
    if (pathname.includes("/search/city/")) {
      const newPath = pathname.replace(
        /\/search\/city\/[^/]+/,
        `/search/city/${pathValue}`
      );

      // Build query string from the provided parameters
      const params = new URLSearchParams();
      Object.entries(queryParams).forEach(([key, value]) => {
        params.set(key, value);
      });

      const queryString = params.toString();
      const newUrl = queryString ? `${newPath}?${queryString}` : newPath;

      console.log("Navigating to:", newUrl);
      router.push(newUrl);
    }
  };

  return {
    updateParam,
    removeParam,
    getParam,
    updateMultipleParams,
    updatePathParam,
    getPathParam,
    updatePathAndParams,
  };
}
